import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const ListarEProductiva = () => {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const navigate = useNavigate();

  const toDate = useCallback((d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("es-CO");
  }, []);

  // Eliminar etapa productiva
  const deleteEPractica = useCallback(async (id) => {
    try {
      const res = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡No podrá revertir este proceso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!res.isConfirmed) return;

      const { data } = await axios.delete(`/api/etapapractica/delete/${id}`);

      await Swal.fire({
        icon: "success",
        title: data?.message || "Eliminado correctamente",
        showConfirmButton: false,
        timer: 1500,
      });

      setItems((prev) => prev.filter((x) => x._id !== id));
    } catch (err) {
      console.error("Error al eliminar etapa:", err?.message);
      Swal.fire("Error", "No se pudo eliminar la etapa.", "error");
    }
  }, []);

  const getData = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get("/api/etapapractica/listall");

      const etapasConFunciones = (data?.etapas || []).map((ep) => {
        const aprendizId = ep?.aprendizObjectId?._id;
        const matriculaId = ep?.matriculaObjectId?._id;

        const nombreAprendiz = [
          ep?.aprendizObjectId?.nombre1Aprendiz,
          ep?.aprendizObjectId?.apellido1Aprendiz,
          ep?.aprendizObjectId?.apellido2Aprendiz,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        const nombreInstructor = [
          ep?.instructorObjectId?.nombre1Instructor,
          ep?.instructorObjectId?.nombre2Instructor,
          ep?.instructorObjectId?.apellido1Instructor,
          ep?.instructorObjectId?.apellido2Instructor,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        const nombreJefe = [
          ep?.jefeObjectId?.nombre1Jefe,
          ep?.jefeObjectId?.apellido1Jefe,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        return {
          ...ep,
          documentoAprendiz: ep?.aprendizObjectId?.documentoAprendiz ?? "—",
          nombreAprendiz: nombreAprendiz || "—",
          codMatricula: ep?.matriculaObjectId?.codMatricula ?? "—",
          ente: ep?.enteObjectId?.nombreEnteconformador ?? "—",
          instructor: nombreInstructor || "—",
          jefe: nombreJefe || "—",
          fechaInicio: toDate(ep?.fechaInicioEtapaPractica),
          fechaFin: toDate(ep?.fechaFinEtapaPractica),

          // 🔥 ICONOS SIEMPRE EN HORIZONTAL
          funciones: (
            <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">
              {/* 
              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => aprendizId && navigate(`/aprendiz/${aprendizId}`)}
                title="Ver Aprendiz"
                type="button">
                <i className="fa-solid fa-user" />
              </button>
              */}

              <button
                className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => navigate(`/etapaproductiva/bitacoras/${ep._id}/${aprendizId}/${matriculaId}`)}
                title="Bitácoras"
                type="button">
                <i className="fa-solid fa-book" />
              </button>

              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => navigate(`/etapaproductiva/seguimiento/${ep._id}/${aprendizId}/${matriculaId}`)}
                title="Seguimiento"
                type="button">
                <i className="fa-solid fa-clipboard-list" />
              </button>

              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => navigate(`/etapaproductiva/editar/${ep._id}`)}
                title="Editar Etapa"
                type="button">
                <i className="fa-solid fa-pen-to-square" />
              </button>

              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => deleteEPractica(ep._id)}
                title="Eliminar"
                type="button">
                <i className="fa-solid fa-trash" />
              </button>
            </div>
          ),
        };
      });

      setItems(etapasConFunciones);
    } catch (error) {
      console.error("Error al cargar las etapas productivas:", error?.message);
      Swal.fire("Error", "No se pudieron cargar las etapas productivas.", "error");
      setItems([]);
    } finally {
      setCargando(false);
    }
  }, [navigate, toDate, deleteEPractica]);

  useEffect(() => {
    getData();
  }, [getData]);

  const columns = useMemo(
    () => [
      { Header: "Documento", accessor: "documentoAprendiz" },
      { 
        Header: "Aprendiz", 
        accessor: "nombreAprendiz",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "Matrícula", accessor: "codMatricula" },
      { Header: "Alternativa", accessor: "alternativaEtapaPractica" },
      { Header: "Ente", accessor: "ente" },
      { Header: "Instructor", accessor: "instructor" },
      { Header: "Jefe", accessor: "jefe" },
      { Header: "Inicio", accessor: "fechaInicio" },
      { Header: "Fin", accessor: "fechaFin" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Listado de etapas productivas</h5>
                <small className="text-secondary">
                  Seguimiento y control de la etapa práctica del aprendiz.
                </small>
              </div>

              {/* ✅ Acciones */}
              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={cargando}
                  title="Actualizar">
                  <i className={`fa-solid ${cargando ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>
                  Actualizar
                </button>

                {/* ✅ BOTÓN NUEVO */}
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/etapaproductiva/nuevo")}
                  title="Registrar etapa productiva">
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {cargando ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" />
                <div className="mt-3">Cargando etapas productivas…</div>
              </div>
            ) : items.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={items} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🐾</div>
                <div className="fw-semibold">No hay registros</div>
                <div className="small mb-3">Crea el primer registro para iniciar el control.</div>

                {/* ✅ CTA en vacío */}
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/etapaproductiva/nuevo")}>
                  <i className="fa-solid fa-plus me-2"></i>
                  Registrar etapa
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarEProductiva;
